export const TRIGGER_TYPES = {
  EVENT: 'event',
  SCHEDULE: 'schedule',
  MANUAL: 'manual',
  WEBHOOK: 'webhook',
};

export const TRIGGER_TYPE_LABELS = {
  [TRIGGER_TYPES.EVENT]: 'Event',
  [TRIGGER_TYPES.SCHEDULE]: 'Schedule',
  [TRIGGER_TYPES.MANUAL]: 'Manual',
  [TRIGGER_TYPES.WEBHOOK]: 'Webhook',
};

export const NODE_TYPES = {
  TRIGGER: 'trigger',
  CONDITION: 'condition',
  ACTION: 'action',
};

export const EXECUTION_STATUSES = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  TIMEOUT: 'timeout',
};

export const EXECUTION_STATUS_COLORS = {
  [EXECUTION_STATUSES.PENDING]: { variant: 'secondary', className: 'bg-gray-100 text-gray-800' },
  [EXECUTION_STATUSES.RUNNING]: { variant: 'default', className: 'bg-blue-100 text-blue-800' },
  [EXECUTION_STATUSES.COMPLETED]: { variant: 'success', className: 'bg-green-100 text-green-800' },
  [EXECUTION_STATUSES.FAILED]: { variant: 'destructive', className: 'bg-red-100 text-red-800' },
  [EXECUTION_STATUSES.CANCELLED]: { variant: 'secondary', className: 'bg-gray-100 text-gray-800' },
  [EXECUTION_STATUSES.TIMEOUT]: { variant: 'destructive', className: 'bg-orange-100 text-orange-800' },
};

export const EXECUTION_STATUS_LABELS = {
  [EXECUTION_STATUSES.PENDING]: 'Pending',
  [EXECUTION_STATUSES.RUNNING]: 'Running',
  [EXECUTION_STATUSES.COMPLETED]: 'Completed',
  [EXECUTION_STATUSES.FAILED]: 'Failed',
  [EXECUTION_STATUSES.CANCELLED]: 'Cancelled',
  [EXECUTION_STATUSES.TIMEOUT]: 'Timeout',
};

export const OPERATORS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  GREATER_THAN: 'greater_than',
  LESS_THAN: 'less_than',
  GREATER_OR_EQUAL: 'greater_or_equal',
  LESS_OR_EQUAL: 'less_or_equal',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  STARTS_WITH: 'starts_with',
  ENDS_WITH: 'ends_with',
  IN: 'in',
  NOT_IN: 'not_in',
  IS_NULL: 'is_null',
  IS_NOT_NULL: 'is_not_null',
  REGEX_MATCH: 'regex_match',
  DATE_BEFORE: 'date_before',
  DATE_AFTER: 'date_after',
  DATE_BETWEEN: 'date_between',
};

export const OPERATOR_LABELS = {
  [OPERATORS.EQUALS]: 'equals',
  [OPERATORS.NOT_EQUALS]: 'does not equal',
  [OPERATORS.GREATER_THAN]: 'greater than',
  [OPERATORS.LESS_THAN]: 'less than',
  [OPERATORS.GREATER_OR_EQUAL]: 'greater or equal',
  [OPERATORS.LESS_OR_EQUAL]: 'less or equal',
  [OPERATORS.CONTAINS]: 'contains',
  [OPERATORS.NOT_CONTAINS]: 'does not contain',
  [OPERATORS.STARTS_WITH]: 'starts with',
  [OPERATORS.ENDS_WITH]: 'ends with',
  [OPERATORS.IN]: 'is in',
  [OPERATORS.NOT_IN]: 'is not in',
  [OPERATORS.IS_NULL]: 'is empty',
  [OPERATORS.IS_NOT_NULL]: 'is not empty',
  [OPERATORS.REGEX_MATCH]: 'matches pattern',
  [OPERATORS.DATE_BEFORE]: 'is before',
  [OPERATORS.DATE_AFTER]: 'is after',
  [OPERATORS.DATE_BETWEEN]: 'is between',
};

export const OPERATORS_BY_TYPE = {
  string: [OPERATORS.EQUALS, OPERATORS.NOT_EQUALS, OPERATORS.CONTAINS, OPERATORS.NOT_CONTAINS, OPERATORS.STARTS_WITH, OPERATORS.ENDS_WITH, OPERATORS.IN, OPERATORS.NOT_IN, OPERATORS.IS_NULL, OPERATORS.IS_NOT_NULL, OPERATORS.REGEX_MATCH],
  number: [OPERATORS.EQUALS, OPERATORS.NOT_EQUALS, OPERATORS.GREATER_THAN, OPERATORS.LESS_THAN, OPERATORS.GREATER_OR_EQUAL, OPERATORS.LESS_OR_EQUAL, OPERATORS.IN, OPERATORS.IS_NULL, OPERATORS.IS_NOT_NULL],
  boolean: [OPERATORS.EQUALS, OPERATORS.NOT_EQUALS, OPERATORS.IS_NULL, OPERATORS.IS_NOT_NULL],
  date: [OPERATORS.DATE_BEFORE, OPERATORS.DATE_AFTER, OPERATORS.DATE_BETWEEN, OPERATORS.IS_NULL, OPERATORS.IS_NOT_NULL],
  array: [OPERATORS.CONTAINS, OPERATORS.NOT_CONTAINS, OPERATORS.IS_NULL, OPERATORS.IS_NOT_NULL],
};

export const STEP_TYPE_LABELS = {
  [NODE_TYPES.TRIGGER]: 'Trigger',
  [NODE_TYPES.CONDITION]: 'Condition',
  [NODE_TYPES.ACTION]: 'Action',
};

export const LOGICAL_OPERATORS = {
  AND: 'AND',
  OR: 'OR',
};

export const ERROR_BEHAVIORS = {
  STOP: 'stop',
  CONTINUE: 'continue',
  RETRY: 'retry',
};

export const ERROR_BEHAVIOR_LABELS = {
  [ERROR_BEHAVIORS.STOP]: 'Stop execution',
  [ERROR_BEHAVIORS.CONTINUE]: 'Continue to next action',
  [ERROR_BEHAVIORS.RETRY]: 'Retry action',
};

export const PAGINATION_DEFAULTS = {
  PAGE: 1,
  LIMIT: 20,
};

export const TIMEFRAME_OPTIONS = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

export const NODE_STYLES = {
  [NODE_TYPES.TRIGGER]: { borderColor: 'border-blue-500', bgColor: 'bg-blue-50', textColor: 'text-blue-700', iconColor: 'text-blue-500', hex: '#3b82f6' },
  [NODE_TYPES.CONDITION]: { borderColor: 'border-amber-500', bgColor: 'bg-amber-50', textColor: 'text-amber-700', iconColor: 'text-amber-500', hex: '#f59e0b' },
  [NODE_TYPES.ACTION]: { borderColor: 'border-green-500', bgColor: 'bg-green-50', textColor: 'text-green-700', iconColor: 'text-green-500', hex: '#22c55e' },
};

export const MAX_CONDITION_NESTING_DEPTH = 3;

export const DEFAULT_RULE_SETTINGS = {
  priority: 0,
  maxRetries: 3,
  retryDelayMs: 1000,
  timeoutMs: 30000,
  enabled: true,
};

// Palette items for the node sidebar — these define the drag-and-drop options.
// Each has a nodeType (trigger/condition/action), a subtype for specificity,
// and display metadata (label, description, iconName referencing lucide-react).
export const TRIGGER_PALETTE_ITEMS = [
  { nodeType: NODE_TYPES.TRIGGER, subtype: TRIGGER_TYPES.EVENT, label: 'Event', description: 'When an event occurs', iconName: 'Zap' },
  { nodeType: NODE_TYPES.TRIGGER, subtype: TRIGGER_TYPES.SCHEDULE, label: 'Schedule', description: 'On a time schedule', iconName: 'Clock' },
  { nodeType: NODE_TYPES.TRIGGER, subtype: TRIGGER_TYPES.MANUAL, label: 'Manual', description: 'Triggered manually', iconName: 'MousePointer' },
  { nodeType: NODE_TYPES.TRIGGER, subtype: TRIGGER_TYPES.WEBHOOK, label: 'Webhook', description: 'Triggered by external HTTP call', iconName: 'Webhook' },
];

export const CONDITION_PALETTE_ITEMS = [
  { nodeType: NODE_TYPES.CONDITION, subtype: null, label: 'Condition', description: 'If / else branch', iconName: 'GitBranch' },
];

export const ACTION_PALETTE_ITEMS = [
  { nodeType: NODE_TYPES.ACTION, subtype: 'order_update_status', label: 'Update Status', description: 'Change order status', iconName: 'Play' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'order_add_flag', label: 'Add Flag', description: 'Flag an order', iconName: 'Flag' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'order_remove_flag', label: 'Remove Flag', description: 'Remove order flag', iconName: 'Flag' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'order_assign_warehouse', label: 'Assign Warehouse', description: 'Route to warehouse', iconName: 'Truck' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'notification_send_slack', label: 'Send Slack', description: 'Send a Slack message', iconName: 'MessageSquare' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'notification_create_ticket', label: 'Create Ticket', description: 'Open a support ticket', iconName: 'Bell' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'flow_delay', label: 'Delay', description: 'Wait before continuing', iconName: 'Timer' },
  { nodeType: NODE_TYPES.ACTION, subtype: 'flow_trigger_rule', label: 'Trigger Rule', description: 'Run another rule', iconName: 'Link2' },
  ...(process.env.NODE_ENV === 'development' ? [
    { nodeType: NODE_TYPES.ACTION, subtype: 'console_log', label: 'Console Log', description: 'Log data for debugging', iconName: 'Terminal' },
  ] : []),
];
